import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BchLogo } from "./BchLogo";
import {
  CRYPTO_FEE_USD,
  STARTING_CRYPTO_USD,
  STARTING_FIAT_USD,
  TOTAL_DAYS,
} from "../data";
import { formatFee, formatUsd } from "../engine";
import { TOTAL_ROUNDS } from "../hustle/data";
import type { DayMarket, GameMode } from "../types";

export type GameType = "survival" | "hustle";

interface StartScreenProps {
  onStart: (mode: GameMode) => void;
  onStartHustle: () => void;
  markets: DayMarket[];
  priceStatus: "loading" | "live" | "fallback";
}

export function StartScreen({ onStart, onStartHustle, markets, priceStatus }: StartScreenProps) {
  const anchor = markets?.[0]?.priceUsd ?? { BCH: 250, BTC: 79000 };
  const [tab, setTab] = useState<GameType>("survival");

  return (
    <div className="flex flex-col items-center text-center gap-6 animate-screen-enter">
      {/* Hero */}
      <div className="relative w-full -mx-4 px-4 pt-2">
        <div className="bch-glow absolute inset-x-0 -top-6 h-64 -z-10" aria-hidden />

        {/* Hero illustration */}
        <div className="relative mx-auto w-full max-w-sm overflow-hidden rounded-2xl border border-border/60 shadow-lg shadow-primary/10">
          <img
            src="/hero.jpg"
            alt="A lively street where shops and customers pay each other in Bitcoin Cash"
            className="block w-full aspect-[3/2] object-cover"
            width={1536}
            height={1024}
            loading="eager"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent" />
          <BchLogo className="absolute left-1/2 -bottom-7 h-16 w-16 -translate-x-1/2 drop-shadow-xl animate-bch-float" />
        </div>

        <div className="flex flex-col items-center gap-2 pt-10">
          <h1 className="font-display text-4xl font-bold tracking-tight text-foreground">
            Survive on <span className="text-primary">BCH</span>
          </h1>
          <p className="text-base text-muted-foreground max-w-sm">
            Live on peer-to-peer digital cash. Two ways to play — spend your way
            through the week, or run the shop and choose how to get paid.
          </p>
        </div>
      </div>

      {/* Game-type selector */}
      <div className="w-full grid grid-cols-2 gap-2 rounded-xl bg-muted/60 p-1.5">
        <button
          onClick={() => setTab("survival")}
          className={`rounded-lg px-3 py-2.5 text-sm font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            tab === "survival"
              ? "bg-card text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          🎒 Survival
        </button>
        <button
          onClick={() => setTab("hustle")}
          className={`rounded-lg px-3 py-2.5 text-sm font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            tab === "hustle"
              ? "bg-card text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          🏪 Hustle
        </button>
      </div>

      {tab === "hustle" ? (
        <HustlePanel onStartHustle={onStartHustle} />
      ) : (
        <SurvivalPanel
          onStart={onStart}
          anchor={anchor}
          priceStatus={priceStatus}
        />
      )}
    </div>
  );
}

function SurvivalPanel({
  onStart,
  anchor,
  priceStatus,
}: {
  onStart: (mode: GameMode) => void;
  anchor: { BCH: number; BTC: number };
  priceStatus: "loading" | "live" | "fallback";
}) {
  return (
    <div className="w-full flex flex-col items-center gap-6">
      <p className="text-sm text-muted-foreground max-w-sm">
        Make it through {TOTAL_DAYS} days of everyday life —{" "}
        <span className="font-semibold text-foreground">spending</span> and{" "}
        <span className="font-semibold text-foreground">getting paid</span> in digital
        cash. It comes down to <span className="font-semibold text-foreground">fees</span>{" "}
        and <span className="font-semibold text-foreground">volatility</span>, on the way
        out <em>and</em> in.
      </p>

      {/* Live price banner */}
      <div
        className={`w-full flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-medium ${
          priceStatus === "live"
            ? "bg-primary/10 text-primary"
            : "bg-muted/70 text-muted-foreground"
        }`}
      >
        <span
          className={`inline-block h-2 w-2 rounded-full ${
            priceStatus === "live"
              ? "bg-primary animate-pulse"
              : priceStatus === "loading"
                ? "bg-muted-foreground/50 animate-pulse"
                : "bg-muted-foreground/50"
          }`}
          aria-hidden
        />
        {priceStatus === "loading"
          ? "Fetching live prices…"
          : priceStatus === "live"
            ? `Live: BCH ${formatUsd(anchor.BCH)} · BTC ${formatUsd(anchor.BTC)}`
            : `Prices unavailable — using ~BCH ${formatUsd(anchor.BCH)} · BTC ${formatUsd(anchor.BTC)}`}
      </div>

      <Card className="w-full">
        <CardContent className="p-5 grid grid-cols-2 gap-4 text-left">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Fiat wallet</p>
            <p className="text-2xl font-bold text-foreground">${STARTING_FIAT_USD}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Crypto wallet</p>
            <p className="text-2xl font-bold text-primary">${STARTING_CRYPTO_USD}</p>
          </div>
          <div className="col-span-2 h-px bg-border" />
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">BCH fee</p>
            <p className="text-lg font-semibold text-primary">~{formatFee(CRYPTO_FEE_USD.BCH)}/tx</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">BTC fee</p>
            <p className="text-lg font-semibold text-foreground">~{formatFee(CRYPTO_FEE_USD.BTC)}/tx</p>
          </div>
          <p className="col-span-2 text-[11px] leading-relaxed text-muted-foreground">
            BCH's fee is flat all week. BTC on-chain is cheap on calm days (~{formatFee(CRYPTO_FEE_USD.BTC)}),
            but <span className="font-medium text-destructive">spikes to $10–$25</span> when the network is
            congested. Lightning stays cheap — but not every payment can use it.
          </p>
        </CardContent>
      </Card>

      <div className="w-full space-y-3">
        <p className="text-sm font-semibold text-foreground">Choose your money:</p>
        <Button
          size="lg"
          className="w-full h-14 text-lg font-bold shadow-lg shadow-primary/25"
          onClick={() => onStart("BCH")}
        >
          Play in BCH Mode 🟢
        </Button>
        <Button
          size="lg"
          variant="secondary"
          className="w-full h-14 text-lg font-bold"
          onClick={() => onStart("BTC")}
        >
          Play in BTC Mode 🟠
        </Button>
        <p className="text-xs text-muted-foreground">
          Both modes play the same 7 days, so you can compare results at the end.
        </p>
      </div>

      <p className="text-[11px] leading-relaxed text-muted-foreground bg-muted/60 rounded-lg px-4 py-3">
        ⚠️ This is a game. Starting prices are <strong>live</strong> (from Coinbase)
        when available, but the day-to-day price drift, fees and scenarios are{" "}
        <strong>simulated</strong>. No real money and no real BCH or BTC are ever used.
      </p>
    </div>
  );
}

function HustlePanel({ onStartHustle }: { onStartHustle: () => void }) {
  return (
    <div className="w-full flex flex-col items-center gap-6">
      <p className="text-sm text-muted-foreground max-w-sm">
        Flip the script: <span className="font-semibold text-foreground">you run the shop</span>.
        Serve {TOTAL_ROUNDS} customers and steer how they pay. Most are open to suggestion —
        convert them to BCH to dodge fees and chargebacks — but a few won't budge.
      </p>

      <Card className="w-full">
        <CardContent className="p-5 space-y-3 text-left">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Ways to get paid
          </p>
          <PayRow icon="🟢" label="BCH" note="Instant · fee <$0.01 · final, no chargebacks" good />
          <PayRow icon="⚡" label="BTC Lightning" note="Cheap & fast — but can fail to route" />
          <PayRow icon="⛓️" label="BTC on-chain" note="Settles, but fees spike when congested" />
          <PayRow icon="💳" label="Card" note="3% + $0.30 fee · reversible for weeks" bad />
        </CardContent>
      </Card>

      <div className="w-full space-y-3">
        <Button
          size="lg"
          className="w-full h-14 text-lg font-bold shadow-lg shadow-primary/25"
          onClick={onStartHustle}
        >
          Open Your Shop 🏪
        </Button>
        <p className="text-xs text-muted-foreground">
          Keep the most money and the happiest customers by the end of the day.
        </p>
      </div>

      <p className="text-[11px] leading-relaxed text-muted-foreground bg-muted/60 rounded-lg px-4 py-3">
        ⚠️ This is a game. All sale amounts, fees, chargebacks and network conditions are{" "}
        <strong>simulated</strong>. No real money and no real BCH or BTC are ever used.
      </p>
    </div>
  );
}

function PayRow({
  icon,
  label,
  note,
  good,
  bad,
}: {
  icon: string;
  label: string;
  note: string;
  good?: boolean;
  bad?: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-lg shrink-0" aria-hidden>{icon}</div>
      <div className="min-w-0">
        <p
          className={`text-sm font-semibold ${
            good ? "text-primary" : bad ? "text-foreground" : "text-foreground"
          }`}
        >
          {label}
        </p>
        <p className="text-xs text-muted-foreground leading-snug">{note}</p>
      </div>
    </div>
  );
}
